// manualDiscounts.js
//
// v3.5.99.7.23 — manual discount engine. A rep can attach one or more
// discount lines to a recurring quote (Managed IT / Voice / Bundle /
// Multi-Site). Each line is either a percentage of the contract MRR or a
// flat $/month amount, and carries a free-text note explaining why
// (e.g. "$50 Discounted Monthly Firewall").
//
// Single source of truth: BOTH the live quote pages (for the on-screen MRR /
// GM badge) AND quoteTotals.buildTotals (for what gets persisted) call
// applyManualDiscounts so the displayed number and the saved number can never
// drift. The signed-document renderers (proposal.js / buildSignatureDocument.js)
// do NOT recompute — they read the resolved lines + net MRR straight from
// totals.
//
// Design decisions (locked with Shaun, 2026-06-09):
//   - Multiple labeled lines (array), not a single discount.
//   - Percentages are always computed against the SAME pre-manual-discount
//     base (the engine MRR), so the order of lines never changes the result
//     and each line's $ impact is independently explainable on the contract.
//   - No min_commitment / $350 floor is applied — a manual discount is a
//     deliberate override. The only guard is the GM warning surfaced on the
//     quote page (warn-but-allow; never blocks document generation).

// Shape of an input discount line (lives on quotes.inputs.manualDiscounts):
//   { id: string, type: 'pct' | 'fixed', value: number, note: string }

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * Apply manual discount lines to a base MRR.
 *
 * @param {number} baseMRR — the contract MRR before manual discounts
 *   (the engine's finalMRR, already net of any contract-term discount).
 * @param {Array} discounts — raw input lines from quotes.inputs.manualDiscounts.
 * @returns {{ finalMRR: number, lines: Array, total: number, base: number }}
 *   - finalMRR: net MRR after all discounts (floored at 0)
 *   - lines: resolved lines [{ type, value, note, amount }] where `amount`
 *     is the computed $/month impact of that line
 *   - total: sum of all line amounts ($/month removed)
 *   - base: the pre-discount MRR, echoed back for display ("was $X")
 */
export function applyManualDiscounts(baseMRR, discounts) {
  const base = round2(baseMRR);
  const lines = [];
  let total = 0;

  const list = Array.isArray(discounts) ? discounts : [];
  for (const d of list) {
    if (!d) continue;
    const value = Number(d.value) || 0;
    if (value <= 0) continue;                 // ignore blank/zero rows
    const type = d.type === 'pct' ? 'pct' : 'fixed';
    // Percentage lines resolve against `base`, not the running balance, so
    // line order is irrelevant and each line is independently auditable.
    const amount = round2(type === 'pct' ? base * (value / 100) : value);
    if (amount <= 0) continue;
    lines.push({ type, value, note: String(d.note || '').trim(), amount });
    total += amount;
  }

  total = round2(total);
  const finalMRR = Math.max(0, round2(base - total));
  return { finalMRR, lines, total, base };
}

/**
 * True when a discount array has at least one effective (value > 0) line.
 * Cheap guard for the pages/renderers so they can skip the discount UI/section.
 */
export function hasManualDiscounts(discounts) {
  return Array.isArray(discounts)
    && discounts.some((d) => d && (Number(d.value) || 0) > 0);
}

/**
 * Normalize an array for persistence: drop empty rows, coerce types, trim
 * notes. Returns [] when nothing effective remains (so we never persist a
 * pile of blank rows).
 */
export function normalizeManualDiscounts(discounts) {
  if (!Array.isArray(discounts)) return [];
  return discounts
    .filter((d) => d && (Number(d.value) || 0) > 0)
    .map((d) => ({
      type:  d.type === 'pct' ? 'pct' : 'fixed',
      value: Number(d.value) || 0,
      note:  String(d.note || '').trim(),
    }));
}

/**
 * Return a discount-adjusted shallow clone of a calc-engine result so a quote
 * page whose entire display reads one `result` object (e.g. Voice) reflects
 * the net MRR everywhere with no scattered edits. finalMRR becomes net and gm
 * is recomputed against it; the original is returned untouched when there are
 * no effective discounts. The save path must still pass the RAW (un-cloned)
 * result + the raw discount array to buildTotals so the discount is applied
 * exactly once.
 */
export function discountedResult(result, discounts) {
  if (!result) return result;
  const md = applyManualDiscounts(result.finalMRR || 0, discounts);
  if (md.total <= 0) return result;
  const cost = Number(result.totalCost) || 0;
  return {
    ...result,
    finalMRR: md.finalMRR,
    gm: md.finalMRR > 0 ? 1 - cost / md.finalMRR : 0,
    manualDiscount: md,
  };
}

/** Short human label for a line, e.g. "10%" or "$50/mo". */
export function discountLineLabel(line) {
  if (!line) return '';
  return line.type === 'pct' ? `${line.value}%` : `$${line.value}/mo`;
}
